"use client";

import Link from "next/link";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { DISTRICTS, DISTRICT_CODES, districtSlug } from "@/lib/districts";

// D1 to D28 in numeric order, whatever order the DISTRICTS map was keyed in.
const NAV_DISTRICT_CODES = [...DISTRICT_CODES].sort(
  (a, b) => Number(a.replace(/^D/i, "")) - Number(b.replace(/^D/i, ""))
);

/**
 * Sitewide nav entry for districts. 28 destinations is too many for
 * the top bar but pure gold for SEO — every page on the site gets 28
 * internal links to per-district landing pages this way.
 *
 * Two display modes, controlled by the `mode` prop:
 *
 *   - "desktop": chevron-button trigger; opens a 4-column flyout
 *     positioned below the header. Hover or click both work.
 *
 *   - "mobile": expands inline as a scrollable list inside the slide-out
 *     menu. Tap the row to toggle.
 *
 * Each district label is "D9 - Orchard / River Valley" so the link
 * text doubles as anchor copy Google can use to weight relevance.
 */
export default function DistrictsNavDropdown({
  mode,
  onNavigate,
}: {
  mode: "desktop" | "mobile";
  /** Mobile menu wants to close itself after a district is picked. */
  onNavigate?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // Horizontal nudge (px) that keeps the centred panel inside the viewport.
  const [shift, setShift] = useState(0);

  useLayoutEffect(() => {
    if (!open || !panelRef.current) return;
    const margin = 16;
    const rect = panelRef.current.getBoundingClientRect();
    // Measure the un-nudged position, then clamp to the viewport.
    const left = rect.left - shift;
    const right = rect.right - shift;
    let next = 0;
    if (left < margin) next = margin - left;
    else if (right > window.innerWidth - margin) next = window.innerWidth - margin - right;
    if (next !== shift) setShift(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Desktop: close on click-outside and on Escape.
  useEffect(() => {
    if (mode !== "desktop" || !open) return;
    const onClick = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, mode]);

  // All 28 links are ALWAYS in the rendered HTML so Googlebot picks
  // them up on its first pass without needing JS execution. The
  // collapsed state hides them visually but they remain crawlable.

  if (mode === "mobile") {
    return (
      <div>
        <button
          type="button"
          onClick={() => setOpen((p) => !p)}
          aria-expanded={open}
          className="flex w-full items-center justify-between py-2 text-gray-600 hover:text-gray-900"
        >
          <span>Districts</span>
          <ChevronDown
            aria-hidden="true"
            className={`h-4 w-4 transition-transform ${
              open ? "rotate-180" : ""
            }`}
          />
        </button>
        <div
          className={`ml-2 border-l border-gray-200 pl-3 overflow-hidden transition-all ${
            open ? "max-h-[1200px] mt-1 mb-2" : "max-h-0"
          }`}
          aria-hidden={!open}
        >
          {NAV_DISTRICT_CODES.map((code) => (
            <Link
              key={code}
              href={`/districts/${districtSlug(code)}`}
              onClick={onNavigate}
              tabIndex={open ? 0 : -1}
              className="block py-1.5 text-sm text-gray-600 hover:text-[#0094FF]"
            >
              {code} - {DISTRICTS[code].label}
            </Link>
          ))}
        </div>
      </div>
    );
  }

  // Desktop
  return (
    <div
      ref={wrapperRef}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      className="relative"
    >
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        aria-expanded={open}
        aria-haspopup="true"
        className="flex items-center gap-1 hover:text-[#0094FF]"
      >
        Districts
        <ChevronDown
          aria-hidden="true"
          className={`h-4 w-4 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      <div
        ref={panelRef}
        // Panel touches the trigger directly (top-full, no margin) so
        // the cursor never crosses a hover-dead gap when moving from
        // the button down into the panel. Visual breathing room comes
        // from the inner `pt-3` instead of an outer margin.
        // Hidden via opacity + invisibility (not display:none — that
        // would remove from DOM and lose the SEO links).
        //
        // Centred under the trigger, then nudged by `shift` so the
        // 720px panel never leaves the viewport (the Districts item
        // sits left of centre, so a right-anchored panel used to run
        // off the left edge on 1440px screens).
        className={`absolute left-1/2 top-full z-50 w-[720px] max-w-[90vw] pt-3 transition-opacity duration-150 ${
          open ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none"
        }`}
        style={{ transform: `translateX(calc(-50% + ${shift}px))` }}
        aria-hidden={!open}
      >
        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-xl grid grid-cols-2 gap-x-6 gap-y-1 md:grid-cols-3 lg:grid-cols-4">
          {NAV_DISTRICT_CODES.map((code) => (
            <Link
              key={code}
              href={`/districts/${districtSlug(code)}`}
              onClick={() => setOpen(false)}
              tabIndex={open ? 0 : -1}
              className="block rounded px-2 py-1.5 text-sm text-gray-700 hover:bg-[#F0F8FF] hover:text-[#0094FF]"
            >
              <span className="font-semibold text-[#0094FF]">{code}</span>{" "}
              <span className="text-gray-600">{DISTRICTS[code].label}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
